import axios from 'axios';

const BASE = '/api/v2/nomenclature';

/** Состояние сверки штрихкода с Национальным каталогом товаров (НКТ) */
export type NktStatus = 'found' | 'ofd' | 'not_found' | 'pending' | 'error' | 'invalid' | 'none';
/** Состояние поиска регистрационного удостоверения в госреестре НЦЭЛС */
export type NcelsStatus = 'found' | 'multiple' | 'not_found' | 'pending' | 'error' | 'skipped' | 'none';
/** Почему штрихкод не GTIN: не цифры / неверная длина, контрольная цифра, внутренний код сети (EAN-13 на 2) */
export type InvalidReason = 'format' | 'check' | 'internal';
export type SortKey = 'name' | 'producer' | 'mnn' | 'sku' | 'type' | 'country';
export type SortDir = 'asc' | 'desc';
export type ImageSize = 'thumb' | 'full';
export type DocumentKind = 'instruction' | 'ohlp';

export interface FilterOption { value: string; count: number }

/** Ход фоновой сверки с источниками (сервис номенклатуры :3004) */
export interface EnrichmentStats {
  barcodes: number;
  valid: number;
  invalid: number;
  nktChecked: number;
  nktFound: number;
  ofdOnly: number;
  drugs: number;
  ncelsChecked: number;
  ncelsFound: number;
  ncelsMultiple: number;
  photos: number;
  marked: number;
}

export interface EnrichmentState {
  running: boolean;
  /** Портал НКТ: ok, no_key - ключ не задан, rejected - ключ отклонён, limited - упёрлись в лимит запросов */
  portal: 'ok' | 'no_key' | 'rejected' | 'limited';
  /** Идёт обновление данных 1С - сверка ждёт его окончания */
  pausedForSync: boolean;
  /** Позиций, отложенных до конца обновления 1С */
  deferred: number;
  lastSeedAt: string | null;
  /** message - только тем, у кого есть право на сверку */
  lastError: { source: string; message?: string; at: string } | null;
  stats: EnrichmentStats | null;
}

export interface NomenclatureMeta {
  success: boolean;
  permissions: { export: boolean; refresh: boolean };
  limits: { page: number; pageMax: number; termMin: number; export: number };
  /** null - сервис номенклатуры недоступен */
  filters: Record<'type' | 'category' | 'priceGroup' | 'folder' | 'rxotc', FilterOption[]> | null;
  data: { lastUpdated: string | null; totalItems: number; status: string | null; isLoading: boolean } | null;
  enrichment: EnrichmentState | null;
  dataError: string | null;
}

/** Данные 1С: без цен и менеджера КМ */
export interface Product1C {
  zid: string;
  sku: string | null;
  name: string;
  producer: string | null;
  mnn: string | null;
  category: string | null;
  barcode: string | null;
  folder: string | null;
  type: string | null;
  priceGroup: string | null;
  rxotc: string | null;
  /** Делимость упаковки (больше 1), иначе null */
  partCoeff: number | null;
  notForMovements: boolean;
  notForReturns: boolean;
}

export interface ProductRow extends Product1C {
  nkt: NktStatus;
  ncels: NcelsStatus;
  invalidReason: InvalidReason | null;
  /** Файл фото НКТ */
  photo: string | null;
  /** Макет упаковки из РУ - если фото НКТ нет */
  boxId: number | null;
  marked: boolean | null;
  country: string | null;
  nktName: string | null;
  regNumber: string | null;
}

export interface ListQuery {
  term: string;
  type: string;
  category: string;
  priceGroup: string;
  folder: string;
  rxotc: string;
  photo: string;
  marked: string;
  nkt: string;
  ncels: string;
  sort: SortKey;
  dir: SortDir;
}

export interface ListResponse { success: boolean; total: number; offset: number; limit: number; items: ProductRow[] }

export interface CodeName { code: string; name: string }

/** Карточка товара в НКТ (nationalcatalog.kz) */
export interface NktCard {
  cards: number;
  gtin: string | null;
  ntin: string | null;
  kztin: string | null;
  nameRu: string | null;
  nameKk: string | null;
  nameEn: string | null;
  shortNameRu: string | null;
  brand: string | null;
  article: string | null;
  country: CodeName | null;
  tnved: CodeName | null;
  /** Путь категорий ОКТРУ */
  oktru: CodeName[] | null;
  measure: string | null;
  packQty: number | string | null;
  producer: { name: string } | null;
  marked: boolean;
  eaes: boolean | null;
  szpt: boolean | null;
  /** Фото, лицевая сторона первой */
  images: Array<{ file: string; type: string | null }>;
  extra: Array<{ name: string; value: string }>;
  createdAt: string | null;
  updatedAt: string | null;
}

/** Ответ ОФД (nct.gov.kz): есть даже там, где карточки НКТ нет */
export interface OfdInfo { nameRu: string | null; ntin: string | null; marked: boolean; status: string | null }

export interface NcelsCandidate {
  id: number;
  regNumber: string | null;
  tradeName: string | null;
  form: string | null;
  producer: string | null;
  country: string | null;
  expirationDate: string | null;
}

export interface Substance { name: string; type: string | null; amount: number | null; measure: string | null }
export interface RegisterBox { id: number; name: string | null; type: string | null; count: number | null; volume: number | null; measure: string | null; units: string | null; description: string | null }
export interface RegisterForm { id: number; dosage: string | null; concentration: string | null; packing: string | null; units: string | null }

/** Регистрационное удостоверение госреестра НЦЭЛС */
export interface Register {
  id: number;
  regNumber: string | null;
  regType: string | null;
  tradeName: string | null;
  inn: string | null;
  shortName: string | null;
  drugType: string | null;
  regAction: string | null;
  regDate: string | null;
  expirationDate: string | null;
  unlimited: boolean;
  producer: string | null;
  producerEn: string | null;
  country: string | null;
  atc: CodeName | null;
  storage: string | null;
  recipe: boolean | null;
  recipeText: string | null;
  gmp: boolean;
  generic: boolean;
  biosimilar: boolean;
  child: boolean;
  sugar: boolean;
  nd: { name: string; number: string } | null;
  dosageForm: string | null;
  concentration: string | null;
  dosageComment: string | null;
  useMethod: string | null;
  additionalFeature: string | null;
  substances: Substance[];
  boxes: RegisterBox[];
  forms: RegisterForm[];
  checkedAt: string | null;
  instructionKind: 'docx' | 'pdf' | 'none';
  /** Разделы инструкции; в тексте абзацы через \n */
  instruction: Array<{ title: string; text: string }> | null;
}

export interface NktSource {
  status: NktStatus;
  checkedAt: string | null;
  /** Срок перепроверки прошёл - данные обновятся */
  updating: boolean;
  error: string | null;
  card: NktCard | null;
  ofd: OfdInfo | null;
  ofdCheckedAt: string | null;
}

export interface NcelsSource {
  status: NcelsStatus;
  checkedAt?: string | null;
  updating?: boolean;
  error?: string | null;
  /** Как найдено РУ: точное название, название + число в упаковке, название + дозировка */
  match?: 'exact' | 'units' | 'dose' | null;
  /** По какому названию искали и откуда оно (НКТ, ОФД, 1С) */
  query?: { from: 'nkt' | 'ofd' | '1c'; name: string; tradeName: string; units?: number | null; dose?: string | null } | null;
  candidates?: NcelsCandidate[] | null;
  register?: Register | null;
}

export interface ProductSources {
  barcode: string;
  gtin: string | null;
  state: 'ok' | 'invalid';
  invalidReason: InvalidReason | null;
  nkt: NktSource;
  ncels: NcelsSource;
}

export interface RelatedProduct { zid: string; name: string; producer: string | null; type: string | null }

export interface ProductResponse {
  success: boolean;
  product: Product1C;
  /** null - у товара нет штрихкода */
  sources: ProductSources | null;
  /** Данных нет или срок перепроверки прошёл: позиция уже в начале очереди, карточку стоит перечитать */
  pending: boolean;
  /** Сверка начнётся после обновления данных 1С */
  waitingSync: boolean;
  sameBarcode: RelatedProduct[];
  /** Товары с тем же МНН */
  analogs: RelatedProduct[];
  analogsTruncated: boolean;
  permissions: { refresh: boolean };
}

const silentParam = (silent: boolean): Record<string, string> => (silent ? { silent: '1' } : {});

/** Условия списка без пустых значений - тело запроса и параметры выгрузки */
function queryParams(q: ListQuery): Record<string, string> {
  const params: Record<string, string> = {};
  (Object.keys(q) as Array<keyof ListQuery>).forEach(key => {
    const value = String(q[key] || '').trim();
    if (value) params[key] = value;
  });
  return params;
}

function fileNameFrom(disposition: unknown, fallback: string): string {
  const header = String(disposition || '');
  const utf = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf) {
    try {
      return decodeURIComponent(utf[1].trim());
    } catch {
      // некорректная кодировка - берём ASCII-имя
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  if (plain) return plain[1];
  return fallback;
}

export interface DownloadedFile { blob: Blob; filename: string }

export const nomenclatureApi = {
  async meta(signal?: AbortSignal): Promise<NomenclatureMeta> {
    const res = await axios.get(`${BASE}/meta`, { signal });
    return res.data;
  },

  async list(q: ListQuery, offset: number, limit: number, silent: boolean, signal?: AbortSignal): Promise<ListResponse> {
    const res = await axios.post(`${BASE}/list`, { ...queryParams(q), offset, limit }, { params: silentParam(silent), signal });
    return res.data;
  },

  async product(zid: string, silent: boolean, signal?: AbortSignal): Promise<ProductResponse> {
    const res = await axios.get(`${BASE}/product/${encodeURIComponent(zid)}`, { params: silentParam(silent), signal });
    return res.data;
  },

  async refresh(zid: string): Promise<{ started: boolean; waitingSync: boolean; message: string }> {
    const res = await axios.post(`${BASE}/refresh`, { zid });
    return res.data;
  },

  async exportFile(q: ListQuery, signal?: AbortSignal): Promise<DownloadedFile> {
    const res = await axios.get(`${BASE}/export`, {
      params: queryParams(q),
      responseType: 'blob',
      // файл может собираться дольше общего таймаута axios, обрыв - через signal
      timeout: 0,
      signal
    });
    return { blob: res.data, filename: fileNameFrom(res.headers['content-disposition'], `nomenclature_${new Date().toISOString().slice(0, 10)}.xlsx`) };
  },

  /** Фото НКТ (kind = nkt, id - имя файла) или макет упаковки РУ (kind = box): сессия - в заголовке, не в адресе картинки */
  async image(kind: 'nkt' | 'box', id: string | number, size: ImageSize, signal?: AbortSignal): Promise<Blob> {
    const res = await axios.get(`${BASE}/image/${kind}/${encodeURIComponent(String(id))}`, { params: { size }, responseType: 'blob', signal });
    return res.data;
  },

  async document(regId: number, kind: DocumentKind, signal?: AbortSignal): Promise<DownloadedFile> {
    const res = await axios.get(`${BASE}/document/${regId}/${kind}`, { responseType: 'blob', timeout: 0, signal });
    return { blob: res.data, filename: fileNameFrom(res.headers['content-disposition'], `${kind}_${regId}`) };
  }
};

export const isCancelled = (err: unknown): boolean => axios.isCancel(err);

/** 403 - нет права на раздел или действие */
export const isForbidden = (err: unknown): boolean => (err as { response?: { status?: number } })?.response?.status === 403;
